import React, { useEffect, useState } from 'react';
import { VictoryChart, VictoryAxis, VictoryBar } from 'victory';
import dayjs from 'dayjs';
import abbreviateNumber from 'utils/abbreviate-number';
import utc from 'dayjs/plugin/utc';
import { checkIfEnoughData, getDataAtPointInTime, getPercentageChange } from 'utils/data-vis-helpers';
import EmptyState from 'components/empty-state';
import TopSectionStats from '../chart-helpers/top-section-stats';
import ChartStatTableRow from '../chart-helpers/bottom-section-stats';
import { buildClassName } from 'utils/build-class-name';
import styles from 'components/charts/address-page-charts.module.scss';
import { lineStyle, lineStyleExp } from '../chart-styles';
import { useThemeContext } from 'contexts';
import { ThemeNames } from 'types/themes';

import type { GraphData, Props } from '../types';

dayjs.extend(utc);

export default function SoldVolume({ listing, chartData, neighbourhoodData, isBlurred }: Props) {
  const { themeName } = useThemeContext();
  const isExpTheme = themeName && themeName !== ThemeNames.ZOOCASA;
  const [hasData, setHasData] = useState(true);
  const [cityData, setCityData] = useState([]);
  const [minValue, setMinValue] = useState<number>(0);
  const [maxValue, setMaxValue] = useState(0);
  const [cityAverageHomePrice, setCityAverageHomePrice] = useState(0);
  const [averageNeighbourhoodHomePrice, setAverageNeighbourhoodHomePrice] = useState(0);

  const [oneMonthChange, setOneMonthChange] = useState('');
  const [threeMonthChange, setThreeMonthChange] = useState('');
  const [sixMonthChange, setSixMonthChange] = useState('');
  const [oneYearChange, setOneYearChange] = useState('');

  const [neighbourhoodOneMonthChange, setNeighbourhoodOneMonthChange] = useState('');
  const [neighbourhoodThreeMonthChange, setNeighbourhoodThreeMonthChange] = useState('');
  const [neighbourhoodSixMonthChange, setNeighbourhoodSixeMonthChange] = useState('');
  const [neighbourhoodOneYearChange, setNeighbourhoodOneYearChange] = useState('');

  const formatSoldPriceChartData = (response: Record<string, any>) => {
    const formattedChartData = response.data.map((r: Record<string, unknown>) => {
      const soldCount = r.soldCount as number;
      if (minValue === 0 && soldCount !== null) {
        setMinValue(soldCount);
      } else if (minValue < soldCount && soldCount !== null) {
        setMinValue(soldCount);
      }

      if (soldCount > maxValue && soldCount !== null) {
        setMaxValue(soldCount);
      }

      return {
        x: r.soldAt,
        y: soldCount,
      } as GraphData;

    });//.filter((data: Record<string, unknown>) => data.x && data.y);
    setOneMonthChange(getDataAtPointInTime(response.data, 1, 'month').soldCount);
    setThreeMonthChange(getDataAtPointInTime(response.data, 3, 'month').soldCount);
    setSixMonthChange(getDataAtPointInTime(response.data, 6, 'month').soldCount);
    setOneYearChange(getDataAtPointInTime(response.data, 1, 'year').soldCount);

    setCityAverageHomePrice(getHomesSoldOverLastYear(formattedChartData));

    return formattedChartData;
  };

  const getHomesSoldOverLastYear = (chartData: any) => {
    return chartData.reduce((totalSold: number, data: Record<string, number>) => {
      return totalSold + data.y;
    }, 0);
  };

  const formatNeighbourhoodData = (response: Record<string, any>) => {
    if (!response.data || !checkIfEnoughData(response.data || [])) {
      setHasData(false);
      return;
    }
    const neighbourhoodFormattedData = response.data.map((r: any) => {
      return {
        y: r.soldCount,
        x: r.soldAt,
      };
    }).filter((data: Record<string, 'x' | 'y'>) => data.x && data.y);
    const currentAverageSoldListingsInMonth = response.data[response.data.length - 2].soldCount;
    setAverageNeighbourhoodHomePrice(getHomesSoldOverLastYear(neighbourhoodFormattedData));
    const oneYearAgo = getDataAtPointInTime(response.data, 1, 'year');
    const oneMonthAgo = getDataAtPointInTime(response.data, 1, 'month');
    const threeMonthAgo = getDataAtPointInTime(response.data, 3, 'month');
    const sixMonthAgo = getDataAtPointInTime(response.data, 6, 'month');
    setNeighbourhoodOneYearChange(getPercentageChange(currentAverageSoldListingsInMonth, oneYearAgo.soldCount));
    setNeighbourhoodOneMonthChange(oneMonthAgo.soldCount);
    setNeighbourhoodThreeMonthChange(threeMonthAgo.soldCount);
    setNeighbourhoodSixeMonthChange(sixMonthAgo.soldCount);
  };

  useEffect(() => {
    if (chartData) {
      setCityData(formatSoldPriceChartData(chartData));
    } else {
      setHasData(false);
    }
    if (neighbourhoodData) {
      formatNeighbourhoodData(neighbourhoodData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  return !cityData.length ? null :(
    <div className={styles.component}>
      {!hasData && <div className={styles.empty}><EmptyState message={'Not Enough Data'} /></div>}
      {hasData &&
      <>
        <TopSectionStats
          isBlurred={isBlurred}
          chartTitle={'Sold Volume'}
          chartDescription="The number of homes that are sold in a given time period"
          cityName={listing.city!}
          cityStat={cityAverageHomePrice}
          cityYoYStat={oneYearChange}
          neighbourhoodName={listing.neighbourhoodName!}
          neighbourhoodStat={averageNeighbourhoodHomePrice}
          neighbourhoodYoYStat={neighbourhoodOneYearChange}
        />
        <section className={styles['chart-container']}>
          <VictoryChart domainPadding={8}>
            <VictoryBar data={cityData} style={isExpTheme ? lineStyleExp : lineStyle} />
            <VictoryAxis dependentAxis
              tickCount={3}
              tickFormat={t => abbreviateNumber(t)}
              style={{
                ticks: { stroke: 'grey', size: 5 },
                axisLabel: { fontSize: 10, padding: 10 },
                tickLabels: { fontSize: 16, padding: 5 },
              }}
            />
            <VictoryAxis
              tickCount={3}
              style={{
                ticks: { stroke: 'grey', size: 5 },
                axisLabel: { fontSize: 10, padding: 10 },
                tickLabels: { fontSize: 16 },
              }}
              tickFormat={t => dayjs(t).utc().format("MMM 'YY")}
            />
          </VictoryChart>
          {isBlurred && <div className={styles['blurred-chart']} />}
        </section>
        <section className={styles.stats}>
          <div className={buildClassName(styles['data-summary'], styles['top-margin'])}>
            <div>
            </div>
            <div className={styles.location}>
              {listing.city}
            </div>
            <div className={styles.location}>
              {listing.neighbourhoodName}
            </div>
            <div className={styles.location}>
              Difference
            </div>
          </div>
          <ChartStatTableRow label='1 Month Ago' isBlurred={isBlurred} city={oneMonthChange} neighbourhood={neighbourhoodOneMonthChange} isPercentage={false} />
          <ChartStatTableRow label='3 Months Ago' isBlurred={isBlurred} city={threeMonthChange} neighbourhood={neighbourhoodThreeMonthChange} isPercentage={false} />
          <ChartStatTableRow label='6 Months Ago' isBlurred={isBlurred} city={sixMonthChange} neighbourhood={neighbourhoodSixMonthChange} isPercentage={false} />
        </section>
      </>
      }
    </div>
  );
}
